import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import format from 'date-fns/format';
import cx from 'classnames';
import { Link } from '@reach/router';
import axiosUI from '../../axiosUI';
import { motion, AnimatePresence } from 'framer-motion';
import { InfoOutlined } from '@material-ui/icons';
import Sidebar from '../Sidebar/Sidebar';
import Topbar from '../Topbar/Topbar';
import Main from '../Main/Main';
import Input, { INPUT_TYPES } from '../Input/Input';
import Button from '../Button/Button';
import PopupWrapper from '../PopupWrapper';
import Loader from '../Loader/Loader';
import { withGlobalContext } from '../../App';
import {
  NOTIFICATIONS,
  SALESCHANNEL_NAME_MAP,
  LOCALE_NAME_MAP,
} from '../../constants';

class TranslationChangeset extends Component {
  static propTypes = {
    country: PropTypes.string,
    language: PropTypes.string,
    navigate: PropTypes.func,
    createNotification: PropTypes.func,
  };

  constructor(props) {
    super(props);
    this.state = {
      selected: {},
      drafts: [],
      showDiscardWarningPopup: false,
      loading: false,
    };
  }

  componentDidMount = () => {
    this.setState({ loading: true }, async () => {
      try {
        const { data } = await axiosUI.get(
          `/api/translations/drafts?salesChannel=${this.props.country}&locale=${this.props.language}`
        );
        this.setState({
          drafts: data,
          selected: data.reduce((map, translation) => {
            map[translation.name] = true;
            return map;
          }, {}),
        });
      } catch (err) {
        console.log(err);
      }

      this.setState({ loading: false });
    });
  };

  toggleDiscardWarningPopup = () => {
    this.setState(prevState => ({
      showDiscardWarningPopup: !prevState.showDiscardWarningPopup,
    }));
  };

  discardChanges = async () => {
    const itemsToDelete = this.getSelectedItems();

    try {
      const response = await axiosUI.delete(`/api/translations/drafts`, {
        data: itemsToDelete,
      });
      const { deletedKeys } = response.data;
      const selected = { ...this.state.selected };
      deletedKeys.forEach(key => {
        delete selected[key];
      });

      this.setState(
        {
          drafts: this.state.drafts.filter(
            draft => !deletedKeys.includes(draft.name)
          ),
          selected,
        },
        () => {
          this.toggleDiscardWarningPopup();
        }
      );

      this.props.createNotification({
        message: `Changes are discarded`,
        type: NOTIFICATIONS.TYPE.INFO,
        timeout: 5000,
      });
    } catch (err) {
      console.log(err);
      this.toggleDiscardWarningPopup();

      this.props.createNotification({
        message: `Failed to discard changes`,
        type: NOTIFICATIONS.TYPE.ERROR,
        timeout: 3000,
      });
    }
  };

  approveChanges = () => {
    this.setState({ loading: true }, async () => {
      const itemsToApprove = this.getSelectedItems();

      try {
        await axiosUI.post(`/api/translations/approve`, itemsToApprove);

        // refetch drafts again to update table data;
        const { data } = await axiosUI.get(
          `/api/translations/drafts?salesChannel=${this.props.country}&locale=${this.props.language}`
        );
        this.setState({
          drafts: data,
          selected: data.reduce((map, translation) => {
            map[translation.name] = false;
            return map;
          }, {}),
        });

        this.props.createNotification({
          message: `Changes are approved`,
          type: NOTIFICATIONS.TYPE.INFO,
          timeout: 5000,
        });
      } catch (err) {
        console.log(err);

        this.props.createNotification({
          message: `Failed to approve changes`,
          type: NOTIFICATIONS.TYPE.ERROR,
          timeout: 3000,
        });
      }

      this.setState({ loading: false });
    });
  };

  publishChanges = () => {
    this.setState({ loading: true }, async () => {
      const itemsToPublish = this.getSelectedItems();

      try {
        await axiosUI.post(`/api/translations/publish`, itemsToPublish);

        // refetch drafts again to update table data;
        const { data } = await axiosUI.get(
          `/api/translations/drafts?salesChannel=${this.props.country}&locale=${this.props.language}`
        );
        this.setState({
          drafts: data,
          selected: data.reduce((map, translation) => {
            map[translation.name] = false;
            return map;
          }, {}),
        });

        this.props.createNotification({
          message: `Changes are published`,
          type: NOTIFICATIONS.TYPE.SUCCESS,
          timeout: 5000,
        });
      } catch (err) {
        console.log(err);

        this.props.createNotification({
          message: `Failed to publish changes`,
          type: NOTIFICATIONS.TYPE.ERROR,
          timeout: 3000,
        });
      }

      this.setState({ loading: false });
    });
  };

  toggleSelectAllRows = initialValue => {
    const selections = this.state.drafts.reduce((map, translation) => {
      map[translation.name] = !initialValue;
      return map;
    }, {});
    this.setState({ selected: selections });
  };

  toggleRowSelection = event => {
    this.setState({
      selected: {
        ...this.state.selected,
        [event.target.name]: event.target.checked,
      },
    });
  };

  getSelectedItems = () => {
    return this.state.drafts.filter(draft => this.state.selected[draft.name]);
  };

  render() {
    const selectedItems = this.getSelectedItems();
    const allAreSelected = selectedItems.length === this.state.drafts.length;
    const nonNullSelectedItems = Boolean(selectedItems.length);
    const canPublishSelectedChanges =
      nonNullSelectedItems &&
      selectedItems.every(
        draft => draft.first_approver && draft.second_approver
      );

    return (
      <Fragment>
        <Sidebar highlightNavItemIndex={1} />
        <Topbar className="translations_changeset__topbar">
          <span className="font-bold text-lg capitalize">
            <Link
              className="bb-1-blue no-underline text-blue"
              to="/translations">
              Translations
            </Link>{' '}
            /{' '}
            <Link
              className="bb-1-blue no-underline text-blue"
              to={`/translations/${this.props.country}/${this.props.language}`}>
              {LOCALE_NAME_MAP[this.props.language]} -{' '}
              {SALESCHANNEL_NAME_MAP[this.props.country]}
            </Link>{' '}
            / Review Changes
          </span>
          <span className="float-right text-right">
            <PopupWrapper
              className="relative"
              isOpen={this.state.showDiscardWarningPopup}
              togglePopup={this.toggleDiscardWarningPopup}>
              <Button
                type="red-transparent"
                className="h-8"
                disabled={!nonNullSelectedItems}
                onClick={this.toggleDiscardWarningPopup}>
                Discard Changes
              </Button>
              <AnimatePresence>
                {this.state.showDiscardWarningPopup && (
                  <motion.div
                    initial={{ opacity: 0, y: '80%' }}
                    animate={{ opacity: 1, y: '100%' }}
                    exit={{ opacity: 0, y: '80%' }}
                    transition={{
                      ease: [0.18, 0.89, 0.39, 1.4],
                      duration: 0.3,
                    }}
                    className="button-popup absolute pin-r px-2 py-4 bg-white shadow-md rounded text-left w-64">
                    <div className="text-center leading-normal">
                      Are you sure you want to discard selected changes?
                    </div>
                    <div className="flex justify-around mt-8">
                      <Button
                        className="h-8"
                        onClick={this.toggleDiscardWarningPopup}>
                        No
                      </Button>
                      <Button
                        type="red-transparent"
                        className="h-8"
                        onClick={this.discardChanges}>
                        Yes, discard them
                      </Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </PopupWrapper>
            <Button
              type="blue"
              className="h-8 h-8 ml-8"
              disabled={!nonNullSelectedItems}
              onClick={this.approveChanges}>
              Approve Changes
            </Button>
            <Button
              type="green"
              className="h-8 h-8 ml-8"
              onClick={this.publishChanges}
              disabled={!canPublishSelectedChanges}>
              Publish Changes
            </Button>
          </span>
        </Topbar>
        <Main className="translations_changeset__main relative">
          <Loader loading={this.state.loading}>
            <div className="inline-flex items-center bg-white p-2 rounded mb-4">
              <div className="w-4 h-4 bg-lightGreen rounded-full mr-2" />
              <div className="mr-6 text-sm">Additions</div>
              <div className="w-4 h-4 bg-lightYellow rounded-full mr-2" />
              <div className="text-sm">Modifications</div>
            </div>
            <table className="text-left w-full border-collapse bg-white">
              <thead>
                <tr>
                  <th className="p-2">
                    <Input
                      id="select_all"
                      name="select_all"
                      type={INPUT_TYPES.CHECKBOX}
                      onChange={() => this.toggleSelectAllRows(allAreSelected)}
                      checked={allAreSelected}
                      inputClassName="box_checkbox"
                    />
                  </th>
                  <th className="p-2 font-normal">Updated By</th>
                  <th className="p-2 font-normal">Updated On</th>
                  <th className="p-2 font-normal">Key</th>
                  <th className="p-2 font-normal max-w-xs">Value</th>
                  <th className="p-2 w-16 font-normal text-right">Approvals</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence initial={false}>
                  {this.state.drafts.map(draft => {
                    let totalApprovals = 0;
                    if (draft.first_approver && draft.first_approver.trim()) {
                      totalApprovals += 1;
                    }
                    if (draft.second_approver && draft.second_approver.trim()) {
                      totalApprovals += 1;
                    }
                    return (
                      <motion.tr
                        exit={{ opacity: 0, x: '30%' }}
                        transition={{
                          ease: [0.18, 0.89, 0.39, 1.4],
                          duration: 0.3,
                        }}
                        positionTransition
                        key={draft.name}
                        className={cx({
                          'bg-lightYellow': draft.operation_type === 'UPDATE',
                          'bg-lightGreen': draft.operation_type === 'CREATE',
                        })}>
                        <td className="p-2">
                          <Input
                            id={draft.name}
                            name={draft.name}
                            type={INPUT_TYPES.CHECKBOX}
                            value={draft.name}
                            onChange={this.toggleRowSelection}
                            checked={this.state.selected[draft.name]}
                            inputClassName="box_checkbox"
                          />
                        </td>
                        <td className="p-2">{draft.modified_by}</td>
                        <td className="p-2">
                          {format(new Date(draft.modified_at), 'd MMM yyyy')}
                        </td>
                        <td className="p-2">{draft.name}</td>
                        <td className="p-2">{draft.content}</td>
                        <td className="draft_approvers p-2">
                          <span>{`${totalApprovals}/2`}</span>
                          {Boolean(totalApprovals) && (
                            <InfoOutlined
                              className="cursor-pointer"
                              fontSize="small"
                            />
                          )}
                          {Boolean(totalApprovals) && (
                            <span className="draft_approvers__list">
                              {draft.first_approver}, {draft.second_approver}
                            </span>
                          )}
                        </td>
                      </motion.tr>
                    );
                  })}
                </AnimatePresence>
              </tbody>
            </table>
          </Loader>
        </Main>
      </Fragment>
    );
  }
}

export default  withGlobalContext(TranslationChangeset);
